/**
 * Categories section: bar chart (Top Categories) + category table from by-category.
 * One ComposableReportWidget instance; optional drill into products via callbacks.
 */
import { Box } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
    ComposableReportWidget,
    type ReportWidgetColumnConfig,
    type ReportWidgetConfig,
    type ReportWidgetInteractionPayload,
    type ReportWidgetItem,
} from 'widgets/ComposableReportWidget';

import type { CategoryBreakdownDto } from 'entities/ReportSavingsSummary';

const TOP_CATEGORIES_COUNT = 10;

type SavingsSummaryCategoriesTableColumnId =
    | 'category'
    | 'totalSpend'
    | 'savings'
    | 'orders'
    | 'items'
    | 'missedSavings';

function categoryToWidgetItem(
    dto: CategoryBreakdownDto,
    index: number,
    uncategorizedLabel: string,
): ReportWidgetItem {
    const id = dto.categoryId ?? `category-${index}`;
    const label = dto.categoryTitle?.trim() || uncategorizedLabel;
    const savingsNum = parseFloat(dto.savingsAmount);
    return {
        id,
        label,
        primaryValue: Number.isNaN(savingsNum) ? 0 : savingsNum,
        secondaryValues: {
            label,
            totalSpend: dto.totalSpend,
            savingsAmount: dto.savingsAmount,
            orderCount: dto.orderCount,
            itemCount: dto.itemCount,
            missedSavingsAmount: dto.missedSavingsAmount,
        },
        meta: { categoryId: dto.categoryId, categoryTitle: dto.categoryTitle },
    };
}

interface SavingsSummaryCategoriesSectionProps {
    byCategory: CategoryBreakdownDto[];
    loading?: boolean;
    onCategorySelect?: (payload: ReportWidgetInteractionPayload) => void;
    disableChartAnimation?: boolean;
    tableOnly?: boolean;
    subheader?: string;
    tableColumnIds?: SavingsSummaryCategoriesTableColumnId[];
    showBottomBorder?: boolean;
}

export const SavingsSummaryCategoriesSection = memo(
    ({
        byCategory,
        loading = false,
        onCategorySelect,
        disableChartAnimation = false,
        tableOnly = false,
        subheader,
        tableColumnIds,
        showBottomBorder = true,
    }: SavingsSummaryCategoriesSectionProps) => {
        const { t } = useTranslation();

        const items: ReportWidgetItem[] = useMemo(() => {
            const uncategorizedLabel = t('savingsSummary.uncategorized');

            return [...byCategory]
                .sort((a, b) => {
                    const aSpend = parseFloat(a.totalSpend) || 0;
                    const bSpend = parseFloat(b.totalSpend) || 0;
                    return bSpend - aSpend;
                })
                .slice(0, TOP_CATEGORIES_COUNT)
                .map((dto, i) =>
                    categoryToWidgetItem(dto, i, uncategorizedLabel),
                );
        }, [byCategory, t]);

        const handleInteraction = useCallback(
            (payload: ReportWidgetInteractionPayload) => {
                onCategorySelect?.(payload);
            },
            [onCategorySelect],
        );

        const tableColumns = useMemo<ReportWidgetColumnConfig[]>(() => {
            const allColumnsById: Record<string, ReportWidgetColumnConfig> = {
                category: {
                    id: 'category',
                    label: t('savingsSummary.categoriesColumnCategory'),
                    valueKey: 'label',
                    format: 'text',
                    bold: true,
                },
                totalSpend: {
                    id: 'totalSpend',
                    label: t('savingsSummary.categoriesColumnTotalSpend'),
                    valueKey: 'totalSpend',
                    format: 'currency',
                },
                savings: {
                    id: 'savings',
                    label: t('savingsSummary.savingsAmount'),
                    valueKey: 'savingsAmount',
                    format: 'currency',
                },
                orders: {
                    id: 'orders',
                    label: t('savingsSummary.categoriesColumnOrders'),
                    valueKey: 'orderCount',
                    format: 'number',
                },
                items: {
                    id: 'items',
                    label: t('savingsSummary.categoriesColumnItems'),
                    valueKey: 'itemCount',
                    format: 'number',
                },
                missedSavings: {
                    id: 'missedSavings',
                    label: t('savingsSummary.missedSavings'),
                    valueKey: 'missedSavingsAmount',
                    format: 'currency',
                },
            };

            const defaultOrder: SavingsSummaryCategoriesTableColumnId[] = [
                'category',
                'totalSpend',
                'savings',
                'orders',
                'items',
                'missedSavings',
            ];

            const orderedIds = tableColumnIds?.length
                ? tableColumnIds
                : defaultOrder;

            return orderedIds
                .map((id) => allColumnsById[id])
                .filter((column): column is ReportWidgetColumnConfig =>
                    Boolean(column),
                );
        }, [t, tableColumnIds]);

        const config: ReportWidgetConfig = useMemo(
            () => ({
                widgetId: 'savings-summary-categories',
                title: t('savingsSummary.categoriesSectionTitle'),
                description: subheader,
                visualization: {
                    enabled: !tableOnly,
                    type: 'bar',
                    options: {
                        palette: 'positive',
                        showLegend: false,
                        valueFormatter: (v) =>
                            new Intl.NumberFormat(undefined, {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            }).format(
                                typeof v === 'number'
                                    ? v
                                    : parseFloat(String(v)) || 0,
                            ),
                    },
                },
                table: {
                    enabled: tableOnly,
                    columns: tableColumns,
                },
                onRowClick: onCategorySelect ? handleInteraction : undefined,
                onChartItemClick:
                    !tableOnly && onCategorySelect
                        ? handleInteraction
                        : undefined,
            }),
            [
                t,
                onCategorySelect,
                handleInteraction,
                tableOnly,
                subheader,
                tableColumns,
            ],
        );

        return (
            <Box
                data-testclass="savings-summary-categories-section"
                data-pdf-section="true"
                sx={{
                    borderBottom: showBottomBorder
                        ? '1px solid #E0E0E0'
                        : 'none',
                    paddingBottom: showBottomBorder ? '40px' : 0,
                }}
            >
                <ComposableReportWidget
                    config={config}
                    items={items}
                    loading={loading}
                    disableChartAnimation={disableChartAnimation}
                />
            </Box>
        );
    },
);

SavingsSummaryCategoriesSection.displayName = 'SavingsSummaryCategoriesSection';
